import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X, Sparkles, AlertTriangle, Beaker, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Affiche jusqu'à 3 bannières empilées pour les divulgations majeures
 * récentes non acquittées.
 */
export default function DisclosureBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('disclosures_dismissed') || '[]'); }
    catch { return []; }
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/disclosures?limit=10`);
        if (cancel) return;
        const recent = (data.disclosures || []).filter(d => {
          if (d.acknowledged) return false;
          const age = Date.now() - new Date(d.detected_at).getTime();
          return age < 14 * 24 * 3600 * 1000;
        });
        setItems(recent.slice(0, 3));
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  const handleDismiss = (key) => {
    const next = [...new Set([...dismissed, key])];
    setDismissed(next);
    localStorage.setItem('disclosures_dismissed', JSON.stringify(next));
  };

  const visible = items.filter(d => !dismissed.includes(d.detected_at));
  if (visible.length === 0) return null;

  return (
    <div data-testid="disclosure-banner-stack">
      {visible.map((d, idx) => <Row key={d.detected_at + idx} d={d} onDismiss={handleDismiss} />)}
    </div>
  );
}

function Row({ d, onDismiss }) {
  const isT4 = d.release_id === 'PURSUE-04';
  const isBiolab = d.slug === 'bio-labs-gabbard';
  const isUap = d.kind === 'uap_release' || (!isBiolab && d.new_docs_count > 0);

  const date = new Date(d.detected_at).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  const Icon = isBiolab ? Beaker : isUap ? FileText : AlertTriangle;
  const tone = isBiolab ? 'biolab' : 'uap';
  const ctaTo = isBiolab || isT4 ? '/divulgations' : '/archives';
  const ctaLabel = isBiolab || isT4 ? "Lire l'article" : 'Consulter';

  let short;
  if (isBiolab) short = '120+ bio-labs · 30 pays · DNI Tulsi Gabbard';
  else if (isT4) short = 'Tranche 4 · Pantex 2015 · Étoile 6 branches · Navette 1996 · 40 fichiers';
  else short = d.title || `${d.new_docs_count} nouveaux documents`;

  return (
    <div className={`disclosure-banner disclosure-banner--${tone} relative`}>
      <div className="mx-auto max-w-[1400px] px-6 py-2 flex items-center gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <span className="h-2 w-2 rounded-full pulse-dot" style={{ background: isBiolab ? '#f87171' : '#fde68a' }} />
          <span className="mono text-[10px] tracking-[0.25em] uppercase text-amber-200 inline-flex items-center gap-1">
            <Icon className="h-3 w-3" />
            {isBiolab ? 'Divulgation Gov.' : 'Nouvelle Divulgation'}
          </span>
        </div>
        <div className="text-1 text-sm flex-1 min-w-0 truncate">
          <span className="font-medium">{short}</span>
          {!isBiolab && d.keywords_matched?.length > 0 && (
            <span className="text-amber-200/80 ml-2 mono text-[10px]">
              [{d.keywords_matched.slice(0, 3).join(' · ')}]
            </span>
          )}
          <span className="text-3 ml-2 hidden md:inline">— {date}</span>
        </div>
        <Link
          to={ctaTo}
          className="mono text-[10px] tracking-widest uppercase px-3 py-1 rounded border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 transition-colors shrink-0 inline-flex items-center gap-1"
        >
          <Sparkles className="h-3 w-3" /> {ctaLabel}
        </Link>
        <button
          onClick={() => onDismiss(d.detected_at)}
          aria-label="Masquer"
          className="text-3 hover:text-1 shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
